import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Stack,
    Typography,
} from "@mui/material";
import {BlobProvider} from "@react-pdf/renderer";
import BigNumber from "bignumber.js";
import {ChevronDown, ChevronUp, Download, Mail} from "lucide-react";
import {useMemo, useState} from "react";
import GlenSnackBar, {type BaseAlert} from "@/components/GlenSnackBar.tsx";
import ConfirmEmailReceiptModal, {
    type EmailRecipient,
} from "@/components/Modal/ConfirmEmailReceiptModal.tsx";
import StatementPdf, {type ReceiptData} from "@/components/Pdf/StatementPdf.tsx";
import {type JWTInstructor, type JWTUser, useUser} from "@/components/Providers/JWTProvider.tsx";
import {type NoticeState, useNotice} from "@/components/Providers/NoticeProvider.tsx";
import type {CartListing} from "@/pages/CartHistoryPage.tsx";

type PreviousOrderCardProps = {
    order: CartListing;
};
const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

type CartHistoryRegistration = {
    id: string;
    status: string | null;
    studentCost: number;
    courseSection: {
        instructorName: string;
        meetingDateStart: string;
        meetingDateEnd: string;
        meetingDaysOfWeek: string;
        course: {
            id: string;
            courseName: string;
            subject: string;
            courseDescription: string | null;
            courseNumber: string | null;
        };
        program: {
            programName: string;
        };
        school: {
            id: number;
            shortName: string;
            fullName: string;
        };
    };
    payment: {
        glenbrookPaymentId: string;
        paymentType: string;
        paymentDate: string;
        transactionId: string | null;
        cost: number;
    };
};

type CartHistoryStudentFee = {
    id: string;
    cost: number;
    status: string;
    fee: {
        id: string;
        name: string;
        description: string;
        feeType: string;
        feeCategory: {
            id: string;
            name: string;
            description: string;
            active: boolean;
            createdAt: string;
            updatedAt: string;
        };
        school: {
            id: number | undefined;
            shortName: string | undefined;
            fullName: string | undefined;
        };
    };
    feePayment: {
        glenbrookPaymentId: string;
        paymentType: string;
        paymentDate: string;
        transactionId: string;
        cost: number;
    };
};

type CartHistoryDetail = {
    id: string;
    cartItems: Array<{
        registration: CartHistoryRegistration | null;
        studentFee: CartHistoryStudentFee | null;
    }>;
};

const mapToStatement = ({
    detail,
    user,
    noticeState,
}: {
    detail: CartHistoryDetail;
    user: JWTUser | JWTInstructor;
    noticeState: NoticeState;
}): ReceiptData => {
    let studentName = "";
    let studentNumber = "";
    if ("studentId" in user) {
        studentName = `${user.nameFirst} ${user.nameLast}`;
        studentNumber = user.studentId;
    } else {
        studentName = user.displayName;
        studentNumber = user.employeeID;
    }
    const totalPaid = detail.cartItems
        .reduce(
            (sum, ci) =>
                sum.plus(ci.registration?.payment.cost ?? ci.studentFee?.feePayment.cost ?? 0),
            BigNumber(0),
        )
        .toNumber();

    return {
        studentName,
        studentNumber,
        totalPaid,
        ilTaxSchoolBlock: noticeState.ilTaxSchoolBlock,
        ilTaxSignOff: noticeState.ilTaxSignOff,
        fees: detail.cartItems
            .map(ci => {
                if (ci.studentFee) {
                    return {
                        dateAssessed: new Date(
                            ci.studentFee.feePayment.paymentDate,
                        ).toLocaleDateString(),
                        feeType: ci.studentFee.fee.name,
                        amount: ci.studentFee.cost,
                        paid: ci.studentFee.feePayment.cost,
                        paymentType: ci.studentFee.feePayment.paymentType,
                    };
                }
                if (ci.registration) {
                    return {
                        dateAssessed: new Date(
                            ci.registration.payment.paymentDate,
                        ).toLocaleDateString(),
                        feeType: ci.registration.courseSection.course.courseName,
                        amount: ci.registration.studentCost,
                        paid: ci.registration.payment.cost,
                        paymentType: ci.registration.payment.paymentType,
                    };
                }
                return null;
            })
            .filter(f => f !== null),
    };
};

type PdfDownloadButtonContentProps = {
    statementData: ReceiptData;
    downloadFilename: string;
    onDownloadComplete: () => void;
};

const PdfDownloadButtonContent = ({
    statementData,
    downloadFilename,
    onDownloadComplete,
}: PdfDownloadButtonContentProps) => {
    return (
        <BlobProvider document={<StatementPdf data={statementData} />}>
            {({url, loading, error}) => {
                if (loading) {
                    return <span>Preparing PDF...</span>;
                }
                if (error) {
                    return <span>Failed to generate PDF</span>;
                }
                if (url) {
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = downloadFilename;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    onDownloadComplete();
                }
                return <span>Preparing PDF...</span>;
            }}
        </BlobProvider>
    );
};

export const PreviousOrderCard = ({order}: PreviousOrderCardProps) => {
    const [expanded, setExpanded] = useState(false);
    const [expandedData, setExpandedData] = useState<CartHistoryDetail | null>(null);
    const [loadingEmail, setLoadingEmail] = useState<boolean>(false);
    const [downloadPdf, setDownloadPdf] = useState<boolean>(false);
    const [showEmailConfirmModal, setShowEmailConfirmModal] = useState<boolean>(false);
    const [snackbar, setSnackbar] = useState<BaseAlert & {show: boolean}>({
        show: false,
        severity: "success",
        message: null,
    });
    const [recipients, setRecipients] = useState<EmailRecipient[]>([]);
    const [loadingRecipients, setLoadingRecipients] = useState<boolean>(false);
    const [noticeState] = useNotice();
    const {user} = useUser();

    const statementData = useMemo(() => {
        if (!(expandedData && user)) {
            return null;
        }

        return mapToStatement({
            detail: expandedData,
            user: user,
            noticeState: noticeState,
        });
    }, [expandedData, user, noticeState]);

    const expand = () => {
        downloadData();
        setExpanded(!expanded);
    };

    const downloadData = () => {
        if (!user?.apiFetch || expandedData) {
            return;
        }

        const url = new URL(`/v1/cart/history/${order.id}`, import.meta.env.VITE_APP_API_ENDPOINT);
        user.apiFetch(url).then(async response => {
            const responseData = (await response.json()) as CartHistoryDetail;
            setExpandedData(responseData);
        });
    };

    const onDownloadReceipt = () => {
        downloadData();
        setDownloadPdf(true);
    };

    const onEmailReceipt = async () => {
        setShowEmailConfirmModal(true);
        setLoadingRecipients(true);
        setRecipients([]);

        try {
            const url = new URL(
                `/v1/cart/emailRecipients/${order.id}`,
                import.meta.env.VITE_APP_API_ENDPOINT,
            );
            const response = await user?.apiFetch(url);
            if (response?.ok) {
                const data = await response.json();
                setRecipients(data.recipients);
            }
        } finally {
            setLoadingRecipients(false);
        }
    };

    const handleConfirmEmailReceipt = async (selectedEmails: string[]) => {
        if (!user?.apiFetch) {
            return;
        }

        setLoadingEmail(true);
        const url = new URL(
            `/v1/cart/emailReceipt/${order.id}`,
            import.meta.env.VITE_APP_API_ENDPOINT,
        );

        try {
            const response = await user.apiFetch(url, {
                method: "PUT",
                body: JSON.stringify({
                    recipients: selectedEmails.length > 0 ? selectedEmails : undefined,
                }),
            });

            if (response.ok) {
                setSnackbar({
                    show: true,
                    severity: "success",
                    message: "Receipt email sent successfully",
                });
            } else {
                throw new Error("Failed to send email");
            }
        } catch {
            setSnackbar({
                show: true,
                severity: "error",
                message: "Failed to send receipt email. Please try again.",
            });
        } finally {
            setLoadingEmail(false);
            setShowEmailConfirmModal(false);
        }
    };

    return (
        <Card sx={{mb: 2, boxShadow: 2}}>
            <CardContent>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                    }}
                >
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Purchase #{order.glenbrookPaymentId}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Purchase Date: {formatDate(order.updatedAt)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Payment Method: {order.paymentType}
                        </Typography>
                        {order.transactionId !== null && (
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Transaction ID: {order.transactionId}
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: 1,
                        }}
                    >
                        <Chip label="Completed" color="success" size="small" />
                        <Typography variant="h6" color="primary">
                            ${(order.paidCents / 100).toFixed(2)}
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{my: 2}} />

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        "&:hover": {bgcolor: "action.hover"},
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        mb: 1,
                    }}
                    onClick={expand}
                >
                    <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                        Items Purchased ({order.itemCount})
                    </Typography>
                    <IconButton size="small">
                        {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </IconButton>
                </Box>

                <Collapse in={expanded}>
                    {expandedData === null && <CircularProgress />}
                    {expandedData !== null && <ItemList order={expandedData} />}
                </Collapse>

                <Stack direction="row" spacing={2} sx={{justifyContent: "flex-end"}}>
                    <Button
                        variant="outlined"
                        startIcon={<Mail size={16} />}
                        onClick={() => onEmailReceipt()}
                        size="small"
                        disabled={loadingEmail}
                    >
                        {loadingEmail ? <CircularProgress /> : "Email Receipt"}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Download size={16} />}
                        onClick={() => onDownloadReceipt()}
                        size="small"
                    >
                        {downloadPdf && statementData !== null ? (
                            <PdfDownloadButtonContent
                                statementData={statementData}
                                downloadFilename={`${statementData.studentNumber} - ${order.glenbrookPaymentId} - receipt.pdf`}
                                onDownloadComplete={() => setDownloadPdf(false)}
                            />
                        ) : (
                            "Download Receipt"
                        )}
                    </Button>
                </Stack>
            </CardContent>
            <ConfirmEmailReceiptModal
                show={showEmailConfirmModal}
                handleClose={() => setShowEmailConfirmModal(false)}
                handleConfirm={handleConfirmEmailReceipt}
                recipients={recipients}
                loading={loadingRecipients}
            />
            <GlenSnackBar
                show={snackbar.show}
                severity={snackbar.severity}
                message={snackbar.message}
                onClose={() => setSnackbar(prev => ({...prev, show: false}))}
            />
        </Card>
    );
};

const ItemList = ({order}: {order: CartHistoryDetail}) => {
    return (
        <List dense sx={{mb: 2}}>
            {order.cartItems.map(item => {
                let name = "";
                let price = 0;
                let paid = 0;
                let catagory = "";
                if (item.studentFee) {
                    name = item.studentFee.fee?.name;
                    price = item.studentFee.cost ?? 0;
                    paid = item.studentFee.feePayment?.cost ?? 0;
                    catagory = item.studentFee.fee.feeCategory.name ?? "";
                } else if (item.registration) {
                    catagory = item.registration.courseSection?.program?.programName ?? "";
                    name += ` ${item.registration.courseSection?.course?.courseNumber ?? ""} • ${item.registration.courseSection?.course?.courseName ?? ""}`;
                    price = item.registration.studentCost ?? 0;
                    paid = item.registration.payment?.cost ?? 0;
                }

                return (
                    <ListItem key={item.studentFee?.id ?? item.registration?.id} sx={{px: 0}}>
                        <ListItemText
                            primary={
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography variant="body2">{name}</Typography>
                                    <Typography variant="body2" sx={{fontWeight: 500}}>
                                        ${paid.toFixed(2)}
                                    </Typography>
                                </Box>
                            }
                            secondary={
                                <Typography variant="caption" color="text.secondary">
                                    {catagory} • ${price.toFixed(2)}
                                </Typography>
                            }
                        />
                    </ListItem>
                );
            })}
        </List>
    );
};
